import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from './Button';
import './Header.css';

export const Header = () => {
  const { user, userProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [synesthesiaDropdownOpen, setSynesthesiaDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const synesthesiaDropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (synesthesiaDropdownRef.current && !synesthesiaDropdownRef.current.contains(event.target)) {
        setSynesthesiaDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        // Don't close if clicking the hamburger button or backdrop (backdrop handles its own close)
        if (!event.target.closest('.hamburger-button') && !event.target.closest('.mobile-menu-backdrop')) {
          setMobileMenuOpen(false);
        }
      }
    };

    if (dropdownOpen || synesthesiaDropdownOpen || mobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [dropdownOpen, synesthesiaDropdownOpen, mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('mobile-menu-open');
    } else {
      document.body.classList.remove('mobile-menu-open');
    }
    return () => {
      document.body.classList.remove('mobile-menu-open');
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setDropdownOpen(false);
    setSynesthesiaDropdownOpen(false);
    setMobileMenuOpen(false);
  };

  const handleMobileLinkClick = (e) => {
    setMobileMenuOpen(false);
    setDropdownOpen(false);
    setSynesthesiaDropdownOpen(false);
    
    // Handle anchor links with smooth scroll
    const href = e.currentTarget.getAttribute('href');
    if (href && href.startsWith('/#')) {
      e.preventDefault();
      const targetId = href.substring(2);
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // If element not found, navigate to home first
        navigate('/');
        setTimeout(() => {
          const targetElement = document.getElementById(targetId);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    }
  };

  const getUserFirstName = () => {
    const displayName = userProfile?.display_name?.trim();
    if (displayName) {
      return displayName.split(/\s+/)[0];
    }
    if (userProfile?.username) {
      return userProfile.username;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const getPublicProfileUrl = () => {
    if (userProfile?.username) {
      return `/${userProfile.username}`;
    }
    return null;
  };

  const handleFeaturesNavClick = (e) => {
    handleMobileLinkClick(e);
    if (window.location.pathname === '/') {
      e.preventDefault();
      const featuresElement = document.getElementById('features');
      if (featuresElement) {
        featuresElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  const selfDiagnoseNavItem = (
    <li>
      <Link to="/synesthesia-self-diagnose" className="nav-link" onClick={handleMobileLinkClick}>
        Self-Diagnose
      </Link>
    </li>
  );

  const communityNavItem = (
    <li>
      <Link to="/community" className="nav-link" onClick={handleMobileLinkClick}>
        Community
      </Link>
    </li>
  );

  const guestMarketingNavItems = (
    <>
      {selfDiagnoseNavItem}
      {communityNavItem}
      <li>
        <Link to="/#features" className="nav-link" onClick={handleFeaturesNavClick}>
          Features
        </Link>
      </li>
      <li>
        <Link to="/about" className="nav-link" onClick={handleMobileLinkClick}>
          About
        </Link>
      </li>
    </>
  );

  return (
    <header className="header">
      <div className="header-container">
        <Link to={user ? "/visualize" : "/"} className="logo" onClick={handleMobileLinkClick}>
          <div className="logo-icon">
            {Array.from({ length: 16 }, (_, i) => (
              <div
                key={i}
                className="logo-square"
                style={{
                  backgroundColor: `hsl(${(i * 22.5) % 360}, 70%, ${50 + (i % 3) * 10}%)`
                }}
              />
            ))}
          </div>
          <span className="logo-text">Synesthetic.ca</span>
        </Link>

        <button
          className="hamburger-button"
          onClick={() => {
            setMobileMenuOpen(!mobileMenuOpen);
            // Close other dropdowns when opening mobile menu
            if (!mobileMenuOpen) {
              setDropdownOpen(false);
              setSynesthesiaDropdownOpen(false);
            }
          }}
          aria-label="Toggle menu"
          aria-expanded={mobileMenuOpen}
        >
          <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
          <span className={`hamburger-line ${mobileMenuOpen ? 'open' : ''}`}></span>
        </button>

        {mobileMenuOpen && (
          <div 
            className="mobile-menu-backdrop" 
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <nav className={`nav ${mobileMenuOpen ? 'mobile-open' : ''}`} ref={mobileMenuRef}>
          {user ? (
            <>
              <ul className="nav-links">
                <li><Link to="/visualize" className="nav-link" onClick={handleMobileLinkClick}>Visualizer</Link></li>
                {selfDiagnoseNavItem}
                {communityNavItem}
                <li>
                  <div className="synesthesia-dropdown" ref={synesthesiaDropdownRef}>
                    <button
                      className="synesthesia-menu-button"
                      onClick={() => setSynesthesiaDropdownOpen(!synesthesiaDropdownOpen)}
                    >
                      <span>My Synesthesia</span>
                      <span className={`dropdown-chevron ${synesthesiaDropdownOpen ? 'open' : ''}`}></span>
                    </button>
                    {synesthesiaDropdownOpen && (
                      <div className="dropdown-menu">
                        {getPublicProfileUrl() ? (
                          <Link
                            to={getPublicProfileUrl()}
                            className="dropdown-item"
                            onClick={handleMobileLinkClick}
                          >
                            View Public Profile
                          </Link>
                        ) : (
                          <div className="dropdown-item disabled" style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                            View Public Profile
                            <span style={{ fontSize: '12px', display: 'block', marginTop: '4px', color: 'var(--text-secondary)' }}>
                              Set username in Edit Profile
                            </span>
                          </div>
                        )}
                        <Link
                          to="/saved"
                          className="dropdown-item"
                          onClick={handleMobileLinkClick}
                        >
                          My Saved Items
                        </Link>
                        <Link
                          to="/color-profile"
                          className="dropdown-item"
                          onClick={handleMobileLinkClick}
                        >
                          My Color Profile
                        </Link>
                      </div>
                    )}
                  </div>
                </li>
              </ul>
              <div className="auth-buttons">
                <div className="user-dropdown" ref={dropdownRef}>
                  <button
                    className="user-menu-button"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                  >
                    <span>Hello, {getUserFirstName()}!</span>
                    <span className={`dropdown-chevron ${dropdownOpen ? 'open' : ''}`}></span>
                  </button>
                  {dropdownOpen && (
                    <div className="dropdown-menu">
                      <Link
                        to="/profile/edit"
                        className="dropdown-item"
                        onClick={handleMobileLinkClick}
                      >
                        Edit Profile
                      </Link>
                      <Link
                        to="/settings"
                        className="dropdown-item"
                        onClick={handleMobileLinkClick}
                      >
                        Account Settings
                      </Link>
                      <div className="dropdown-divider"></div>
                      <button
                        className="dropdown-item"
                        onClick={handleLogout}
                      >
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <>
              <ul className="nav-links">
                <li><Link to="/" className="nav-link" onClick={handleMobileLinkClick}>Visualizer</Link></li>
                {guestMarketingNavItems}
              </ul>
              <div className="auth-buttons">
                <Link to="/signin" onClick={handleMobileLinkClick}>
                  <Button variant="secondary" size="medium" className="header-auth-btn">
                    Sign In
                  </Button>
                </Link>
                <Link to="/signup" onClick={handleMobileLinkClick}>
                  <Button variant="primary" size="medium" className="header-auth-btn">
                    Sign Up Free →
                  </Button>
                </Link>
              </div>
            </>
          )}
        </nav>
      </div>
    </header>
  );
};

